import _ from 'lodash';
import {
  PROFILE_SEARCH_NORMALIZE_AMPLITUDE,
  PROFILE_SEARCH_NORMALIZE_LOCATION,
  PROFILE_SEARCH_SIMILARITY,
} from '@/trendData/trendData.constants';
import type { BaseToolPersistedState, ParameterDefinitions, ToolItem } from '@/toolSelection/baseTool.store';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import type { InitializeMode } from '@/core/flux.service';

type SelectedRegion = { min: number; max: number } | null;

interface ProfileSearchPersistedState extends BaseToolPersistedState {
  inputSignal?: ToolItem | null;
  similarity: string;
  normalizeAmplitude: boolean;
  normalizeLocation: boolean;
  selectedRegion: SelectedRegion;
}

export class ProfileSearchStore extends BaseToolStore {
  static readonly storeName = 'sqProfileSearchStore';
  type = TREND_TOOLS.PROFILE_SEARCH;
  parameterDefinitions: ParameterDefinitions = {
    inputSignal: { predicate: ['name', 'a'] },
  };

  initialize(initializeMode: InitializeMode) {
    const saveState = this.state && initializeMode !== 'FORCE';
    this.state = this.immutable(
      _.assign({}, BASE_TOOL_COMMON_PROPS, {
        ...this.parameterDefinitions,
        similarity: PROFILE_SEARCH_SIMILARITY,
        normalizeAmplitude: PROFILE_SEARCH_NORMALIZE_AMPLITUDE,
        normalizeLocation: PROFILE_SEARCH_NORMALIZE_LOCATION,
        selectedRegion: saveState ? (this.state.get('selectedRegion') as SelectedRegion) : null,
      }),
    );
  }

  /**
   * Exports the state that is not ephemeral (such as progress indicators).
   * @returns {Object} The dehydrated state.
   */
  dehydrate(): ProfileSearchPersistedState {
    return {
      ...this.getBasePersistedState(),
      inputSignal: (this.state.get('inputSignal') as ToolItem | null) ?? null,
      similarity: this.similarity,
      normalizeAmplitude: this.normalizeAmplitude,
      normalizeLocation: this.normalizeLocation,
      selectedRegion: (this.state.get('selectedRegion') as SelectedRegion) ?? null,
    };
  }

  /**
   * Re-creates the profile search results.
   *
   * @param {Object} dehydratedState Previous state usually obtained from `dehydrate` method.
   */
  rehydrate(dehydratedState: Partial<ProfileSearchPersistedState>) {
    this.applyBasePersistedState(dehydratedState);

    if ('inputSignal' in dehydratedState) {
      this.state.set('inputSignal', dehydratedState.inputSignal ?? null);
    }
    if ('similarity' in dehydratedState && dehydratedState.similarity !== undefined) {
      this.state.set('similarity', dehydratedState.similarity);
    }
    if ('normalizeAmplitude' in dehydratedState && dehydratedState.normalizeAmplitude !== undefined) {
      this.state.set('normalizeAmplitude', dehydratedState.normalizeAmplitude);
    }
    if ('normalizeLocation' in dehydratedState && dehydratedState.normalizeLocation !== undefined) {
      this.state.set('normalizeLocation', dehydratedState.normalizeLocation);
    }
    if ('selectedRegion' in dehydratedState && dehydratedState.selectedRegion !== undefined) {
      this.state.set('selectedRegion', dehydratedState.selectedRegion);
    }
  }

  get inputSignal(): ToolItem | undefined {
    return this.state.get('inputSignal');
  }

  get selectedRegion(): SelectedRegion {
    return this.state.get('selectedRegion') as SelectedRegion;
  }

  get similarity(): string {
    return this.state.get('similarity');
  }

  get normalizeAmplitude(): boolean {
    return this.state.get('normalizeAmplitude');
  }

  get normalizeLocation(): boolean {
    return this.state.get('normalizeLocation');
  }

  protected readonly handlers: BaseToolStore['handlers'] & {
    PROFILE_SEARCH_SET_SIMILARITY: (payload: { similarity: string }) => void;
    PROFILE_SEARCH_SET_NORMALIZE_AMPLITUDE: (payload: { normalizeAmplitude: boolean }) => void;
    PROFILE_SEARCH_SET_NORMALIZE_LOCATION: (payload: { normalizeLocation: boolean }) => void;
    TREND_SET_SELECTED_REGION: (payload: SelectedRegion) => void;
  } = {
    ...this.baseHandlers,
    /**
     * Sets the similarity for the profile search.
     * @param {Object} payload - Object containing state information
     * @param {String} payload.similarity - the similarity for the profile search.
     */
    PROFILE_SEARCH_SET_SIMILARITY: (payload: { similarity: string }) => {
      this.state.set('similarity', payload.similarity);
    },
    /**
     * Ignore amplitude of candidates in the profile search.
     * @param {Object} payload - Object containing state information
     * @param {Boolean} payload.normalizeAmplitude - normalize amplitude for profile search.
     */
    PROFILE_SEARCH_SET_NORMALIZE_AMPLITUDE: (payload: { normalizeAmplitude: boolean }) => {
      this.state.set('normalizeAmplitude', payload.normalizeAmplitude);
    },
    /**
     * Ignore location of candidates in the profile search.
     * @param {Object} payload - Object containing state information
     * @param {Boolean} payload.normalizeLocation - normalize location for profile search.
     */
    PROFILE_SEARCH_SET_NORMALIZE_LOCATION: (payload: { normalizeLocation: boolean }) => {
      this.state.set('normalizeLocation', payload.normalizeLocation);
    },
    /**
     * Sets the selected region
     * @param {Object} payload - Object containing state information
     * @param {Number} payload.min - Lower x-value of selected region.
     * @param {Number} payload.max - Upper x-value of selected region.
     */
    TREND_SET_SELECTED_REGION: (payload: SelectedRegion) => {
      this.state.set('selectedRegion', payload);
    },
  };
}
